import React from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { format } from "date-fns";

// Utility function for formatting dates
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return format(date, "dd/MM/yyyy");
};

const primaryResources = [
  {
    rid: "053cea08-09bc-40ec-8f7a-156f0677aff3",
    chassisField: "misgeret",
    filters: ({ text }) => ({
      mispar_rechev: text,
    }),
    secondaryApiTag: "CAR",
    secondaryResourcePayloadKeys: ["tozeret_cd", "degem_nm", "shnat_yitzur"],
  },
  {
    rid: "0866573c-40cd-4ca8-91d2-9dd2d7a492e5",
    chassisField: null, // tire-codes registry, no chassis column
    filters: ({ text }) => ({
      mispar_rechev: text,
    }),
    secondaryApiTag: "CAR",
    secondaryResourcePayloadKeys: ["tozeret_cd", "degem_cd", "sug_degem"],
  },
  {
    rid: "03adc637-b6fe-402b-9937-7c3d3afc9140",
    chassisField: "misgeret",
    filters: ({ text }) => ({
      mispar_rechev: text,
    }),
    secondaryApiTag: "CAR",
    secondaryResourcePayloadKeys: ["tozeret_cd", "degem_nm", "shnat_yitzur"],
  },
  {
    rid: "f6efe89a-fb3d-43a4-bb61-9bf12a9b9099",
    chassisField: "misgeret",
    filters: ({ text }) => ({
      mispar_rechev: text,
    }),
    secondaryApiTag: "CAR",
    secondaryResourcePayloadKeys: ["tozeret_cd", "degem_nm", "shnat_yitzur"],
  },
  {
    rid: "6f6acd03-f351-4a8f-8ecf-df792f4f573a",
    chassisField: "misgeret",
    filters: ({ text }) => ({
      mispar_rechev: text,
    }),
    secondaryApiTag: "CAR",
    secondaryResourcePayloadKeys: ["tozeret_cd", "degem_nm", "shnat_yitzur"],
  },
  {
    rid: "bf9df4e2-d90d-4c0a-a400-19e15af8e95f",
    chassisField: "misgeret",
    filters: ({ text }) => ({
      mispar_rechev: text,
    }),
    secondaryApiTag: "BIKE",
  },
  {
    rid: "cd3acc5c-03c3-4c89-9c54-d40f93c0d790",
    chassisField: "mispar_shilda",
    filters: ({ text }) => ({
      mispar_rechev: text,
    }),
    secondaryApiTag: "TRUCK",
    secondaryResourcePayloadKeys: ["tozeret_cd", "degem_nm", "shnat_yitzur"],
  },
  // --- Vehicles that went off the road (ירדו מהכביש) ---
  // Final-cancellation registry (recent years). Plate stored as a number.
  {
    rid: "851ecab1-0622-4dbe-a6c7-f950cf82abf9",
    chassisField: "misgeret",
    filters: ({ text }) => ({
      mispar_rechev: text,
    }),
    secondaryApiTag: "CAR",
    secondaryResourcePayloadKeys: ["tozeret_cd", "degem_nm", "shnat_yitzur"],
    isOffRoad: true,
  },
  // Older off-road archives. Plate stored as an 8-char zero-padded STRING
  // (e.g. 1173264 -> "01173264"), so pad before filtering.
  {
    rid: "4e6b9724-4c1e-43f0-909a-154d4cc4e046",
    chassisField: "misgeret",
    filters: ({ text }) => ({
      mispar_rechev: String(text).padStart(8, "0"),
    }),
    secondaryApiTag: "CAR",
    secondaryResourcePayloadKeys: ["tozeret_cd", "degem_nm", "shnat_yitzur"],
    isOffRoad: true,
  },
  {
    rid: "ec8cbc34-72e1-4b69-9c48-22821ba0bd6c",
    chassisField: "misgeret",
    filters: ({ text }) => ({
      mispar_rechev: String(text).padStart(8, "0"),
    }),
    secondaryApiTag: "CAR",
    secondaryResourcePayloadKeys: ["tozeret_cd", "degem_nm", "shnat_yitzur"],
    isOffRoad: true,
  },
];

const secondaryResources = [
  {
    rid: "142afde2-6228-49f9-8a29-9b6c3a0cbe40",
    filters: (filters) => filters,
    tags: ["CAR"],
  },
];

const searchLicenseFromBackup = async (licenseNumber) => {
  try {
    const res = await axios.post(`${BASE_URL}/customer/licenseDetails`, {
      licenseNumber,
    });
    return {
      success: !!res?.data?.result,
      licenseData: res?.data?.result?.data,
      secondaryApiTag: res?.data?.result?.vehicle,
    };
  } catch (error) {
    console.log(`Error fetching data from backup API:`, error);
    return {
      success: false,
      licenseData: null,
      secondaryApiTag: null,
    };
  }
};

export const fieldNames = {
  mispar_rechev: "מספר רכב",

  tozeret_nm: "יצרן",
  kinuy_mishari: "דגם",
  shnat_yitzur: "שנת יצור",

  sug_delek_nm: "סוג דלק",
  nefah_manoa: "נפח מנוע",
  nefach_manoa: "נפח מנוע",
  mispar_dlatot: "מספר דלתות",
  degem_manoa: "דגם מנוע",
  moed_aliya_lakvish: "מועד עלייה לכביש",
  ramat_gimur: "רמת גימור",

  degem_cd: "קוד דגם",
  degem_nm: "שם דגם",
  tozeret_cd: "קוד יצרן",
  sug_degem: "סוג דגם",

  // Heavy-vehicles registry (cd3acc5c) field names
  mispar_shilda: "מספר שילדה",
  tozeret_eretz_nm: "ארץ ייצור",
  tkina_EU: "קטגוריה אירופית",
  kvutzat_sug_rechev: "קבוצת סוג רכב",
  mishkal_azmi: "משקל עצמי",
  mishkal_mitan_harama: "משקל מטען הרמה",
  mispar_mekomot: "מספר מקומות",
  mispar_mekomot_leyd_nahag: "מספר מקומות ליד נהג",
  grira_nm: "וו גרירה",
  mispar_manoa: "מספר מנוע",
  ramat_eivzur_betihuty: "רמת אבזור בטיחותי",
  kvutzat_zihum: "קבוצת זיהום",
  mivchan_acharon_dt: "טסט אחרון",
  tokef_dt: "תוקף רישיון רכב",
  bitul_dt: "תאריך ירידה מהכביש",
  baalut: "בעלות",
  misgeret: "מספר שילדה",
  tzeva_cd: "קוד צבע",
  tzeva_rechev: "צבע רכב",
  zmig_kidmi: "צמיג קדמי",
  zmig_ahori: "צמיג אחורי",
  horaat_rishum: "הוראת רישום",
  hanaa_nm: "הנעה",
  delek_nm: "דלק",
  mazgan_ind: "מזגן",
  technologiat_hanaa_nm: "טכנולוגית הנעה",
  kosher_grira_bli_blamim: "כושר גלילה בלי בלמים",
  kvuzat_agra_cd: "קבוצת אגרה",
  merkav: "מרכב",
  automatic_ind: "תיבת הילוכים",
  mispar_moshavim: "מספר מושבים",
  mishkal_kolel: "משקל",
  koah_sus: "כוס סוס",
  abs_ind: "ABS",
  kosher_grira_im_blamim: "כושר גרירה עם בלמים",
};

export const engineType = {
  ["בנזין"]: "GASOLINE",
  ["טורבו בנזין"]: "TURBO GASOLINE",
  ["דיזל"]: "DIESEL",
  ["טורבו דיזל"]: "TURBO DIESEL",
  ["היברידי"]: "HYBRID",
  ["חשמל"]: "ELECTRICAL POWER",
  ["חשמל/בנזין"]: "ELECTRICITY/GASOLINE",
  ['גפ"מ']: "GAPAM",
};

export const gearType = {
  ["0"]: "MANUAL",
  ["1"]: "AUTOMATIC",
  ["2"]: "TIPTONIC",
  ["3"]: "ROBOTIC",
};

const searchLicense = async ({ rid, type = "GENERAL", filters }) => {
  let baseUrl = "";
  if (type === "TRUCK") {
    baseUrl = `https://data.gov.il/api/3/action/datastore_search?resource_id=${rid}&filters=${JSON.stringify(
      filters
    )}`;
  } else {
    baseUrl = `https://data.gov.il/api/action/datastore_search?resource_id=${rid}&filters=${JSON.stringify(
      filters
    )}`;
  }

  // api call
  try {
    const res = await axios.get(baseUrl);
    return res;
  } catch (error) {
    console.log(error);
  }
};

export const useLicenseSearch = () => {
  const [loading, setLoading] = React.useState(false);
  const licenseExtraDataRef = React.useRef({});

  // Query ALL primary registries in parallel and return the first hit by
  // priority order (active registries first, off-road archives last).
  // Much faster than the old sequential chain (~2s instead of 10-20s).
  // Input containing letters is treated as a chassis/VIN number and searched
  // against each registry's chassis column instead of the plate column.
  const searchLicenseByResourceId = React.useCallback(
    async (resourcesArray, _first, text) => {
      const isChassis = /[A-Za-z]/.test(text);
      const candidates = isChassis
        ? resourcesArray.filter((r) => r.chassisField)
        : resourcesArray;
      const results = await Promise.all(
        candidates.map((resource) =>
          searchLicense({
            rid: resource.rid,
            filters: isChassis
              ? { [resource.chassisField]: text.toUpperCase() }
              : resource.filters({ text }),
          })
            .then((res) => ({ resource, res }))
            .catch(() => ({ resource, res: null }))
        )
      );
      for (const { resource, res } of results) {
        if (res && res.data && res.data.result && res.data.result.records.length) {
          return {
            licenseData: res.data.result.records[0],
            secondaryApiTag: resource.secondaryApiTag,
            secondaryResourcePayloadKeys: resource.secondaryResourcePayloadKeys,
            isOffRoad: !!resource.isOffRoad,
          };
        }
      }
      return {
        licenseData: null,
        secondaryApiTag: null,
        secondaryResourcePayloadKeys: null,
        isOffRoad: false,
      };
    },
    []
  );

  const searchLicenseExtraDataByResourceId = React.useCallback(
    async (resourcesArray, { rid, filters }, index, licenseData) => {
      let res = await searchLicense({
        rid,
        filters,
      });
      if (
        res &&
        res.data &&
        res.data.result &&
        res.data.result.records.length
      ) {
        licenseExtraDataRef.current = {
          ...licenseExtraDataRef.current,
          ...res.data.result.records[0],
        };
      }
      if (index === resourcesArray.length - 1) {
        return licenseExtraDataRef.current;
      } else {
        return await searchLicenseExtraDataByResourceId(
          resourcesArray,
          {
            rid: resourcesArray[index + 1].rid,
            filters,
          },
          index + 1,
          licenseData
        );
      }
    },
    []
  );

  const searchForLicense = React.useCallback(
    async (text) => {
      try {
        setLoading(true);

        // if (!text.trim().length && fromSearchForm) {
        //   setLicenseData({});
        //   setFields?.(initalFields);
        //   setDisableAllFields?.(false);
        //   return;
        // }
        // if (text.trim().length < 5 && fromSearchForm) {
        //   return;
        // }
        licenseExtraDataRef.current = {};
        const response = await searchLicenseByResourceId(
          primaryResources,
          {
            rid: primaryResources[0].rid,
            filters: primaryResources[0].filters({ text }),
            secondaryApiTag: primaryResources[0].secondaryApiTag,
            secondaryResourcePayloadKeys:
              primaryResources[0].secondaryResourcePayloadKeys,
          },
          text,
          0
        );
        if (response.licenseData) {
          // When searching by chassis/VIN, continue the flow with the plate
          // number found on the record.
          const foundPlate = String(
            response.licenseData.mispar_rechev || text
          ).replace(/^0+/, "");
          // Some vehicles are missing from the main registry but still match a
          // sparse gov resource (e.g. the tire-codes registry 0866573c, which
          // has no manufacturer/chassis). When the hit lacks core fields,
          // enrich it from our backend snapshot so chassis, model, color and
          // dates are not lost. Registry values win over backend values.
          if (!response.licenseData.tozeret_nm) {
            const backup = await searchLicenseFromBackup(foundPlate);
            if (backup.success && backup.licenseData) {
              response.licenseData = {
                ...backup.licenseData,
                ...response.licenseData,
              };
            }
          }
          const filteredSecondaryApis = secondaryResources.filter((i) =>
            i.tags.includes(response.secondaryApiTag)
          );
          if (filteredSecondaryApis.length) {
            const filtersPayload = response.secondaryResourcePayloadKeys.reduce(
              (a, i) => {
                a[i] = (response.licenseData[i] || "").toString();
                return a;
              },
              {}
            );
            await searchLicenseExtraDataByResourceId(
              filteredSecondaryApis,
              {
                rid: filteredSecondaryApis[0].rid,
                filters: filteredSecondaryApis[0].filters(filtersPayload),
              },
              0,
              response.licenseData
            );
          }
          const licenseData = {
            ...licenseExtraDataRef.current,
            ...response.licenseData,
          };
          const eType = licenseData?.sug_delek_nm || "";
          const gType =
            response.secondaryApiTag === "CAR" && licenseData.degem_cd
              ? String(licenseData?.automatic_ind || "")
              : "";
          console.log("licenseData", licenseData);
          // Format date fields
          const formattedLicenseData = {
            ...licenseData,
            mivchan_acharon_dt: formatDate(licenseData.mivchan_acharon_dt),
            tokef_dt: formatDate(licenseData.tokef_dt),
            moed_aliya_lakvish: formatDate(licenseData.moed_aliya_lakvish),
            ...(licenseData.bitul_dt && {
              bitul_dt: formatDate(licenseData.bitul_dt),
            }),
          };
          return {
            success: true,
            message: "",
            isOffRoad: response.isOffRoad,
            data: {
              ...formattedLicenseData,
              ...(response.secondaryApiTag !== "CAR" && {
                nefah_manoa: "",
                automatic_ind: "",
              }),
            },
            localData: {
              manufacturer: licenseData?.tozeret_nm || "לא צוין",
              model:
                response.secondaryApiTag === "CAR"
                  ? licenseData?.kinuy_mishari ||
                    licenseData?.degem_nm ||
                    "לא צוין"
                  : licenseData?.degem_nm || "",
              subModel: licenseData?.ramat_gimur || licenseData?.degem_nm || "",
              licenseNumber: foundPlate,
              yop: String(licenseData?.shnat_yitzur || ""),
              hp:
                response.secondaryApiTag === "CAR"
                  ? String(
                      licenseExtraDataRef.current?.nefah_manoa ||
                        licenseData.nefach_manoa ||
                        ""
                    )
                  : licenseData?.nefach_manoa || "",
              engineType: engineType[eType] ? engineType[eType] : "",
              gearType: gearType[gType] ? gearType[gType] : "",
              vehicle: response.secondaryApiTag,
            },
          };
        } else {
          const serverResponse = await searchLicenseFromBackup(text);
          if (serverResponse.success) {
            const response = serverResponse;
            // Format date fields from backup response
            const formattedLicenseData = {
              ...response.licenseData,
              mivchan_acharon_dt: formatDate(
                response.licenseData.mivchan_acharon_dt
              ),
              tokef_dt: formatDate(response.licenseData.tokef_dt),
              moed_aliya_lakvish: formatDate(
                response.licenseData.moed_aliya_lakvish
              ),
            };
            return {
              success: true,
              message: "",
              data: formattedLicenseData,
              localData: {
                manufacturer: response.licenseData?.tozeret_nm || "לא צוין",
                model:
                  response.secondaryApiTag === "CAR"
                    ? response.licenseData?.kinuy_mishari ||
                      response.licenseData?.degem_nm ||
                      "לא צוין"
                    : response.licenseData?.degem_nm || "",
                subModel:
                  response.licenseData?.ramat_gimur ||
                  response.licenseData?.degem_nm ||
                  "",
                licenseNumber: text.trim(),
                yop: String(response.licenseData?.shnat_yitzur || ""),
                hp:
                  response.secondaryApiTag === "CAR"
                    ? String(
                        response.licenseData?.nefah_manoa ||
                          response.licenseData.nefach_manoa ||
                          ""
                      )
                    : response.licenseData?.nefach_manoa || "",
                engineType:
                  engineType[response.licenseData?.sug_delek_nm || ""] || "",
                gearType:
                  gearType[
                    response.secondaryApiTag === "CAR" &&
                    response.licenseData.degem_cd
                      ? String(response.licenseData?.automatic_ind || "")
                      : ""
                  ] || "",
                vehicle: response.secondaryApiTag,
              },
            };
          } else {
            return {
              success: false,
              data: {},
              localdata: {},
            };
          }
        }
      } catch (e) {
      } finally {
        setLoading(false);
      }
    },
    [searchLicenseByResourceId, setLoading, searchLicenseExtraDataByResourceId]
  );
  return {
    searchForLicense,
    loading,
  };
};
